import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getCustomersService } from "../../services/customers/services";
import Loading from "../loading/Loading";
import NoResults from "../noResults/noResults";
import ImportCustomer from "../importCustomer/ImportCustomer";
import Pagination from "../pagination/Pagination";
import { IsAllow } from "../../utils/allowHelper/AllowHelper";
import { useTranslator } from "../../context/translator";
import ClientsFilters from "../clientsFilters/ClientsFilters";
import ClientsTable from "../clientsTable/ClientsTable";
import { getCustomerCategoriesByPaginationService } from "../../services/categories/services";
import { PERMISSION_CODE } from "../../utils/enum/enums";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const TableClients = () => {
  const { user } = useSelector((state) => state.user);
  const [companyAllow, setCompanyAllow] = useState([]);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const navigate = useNavigate();

  const { translate } = useTranslator();

  const [clientsData, setClientsData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [importModal, setImportModal] = useState(false);

  const toggleImportCustomers = () => setImportModal((prev) => !prev);

  const handleUpdatePage = (newPage) => {
    if (newPage >= 1 && newPage <= pageNumber) {
      setPage(newPage);
      const params = new URLSearchParams(window.location.search);
      params.set("page", newPage);
      navigate(`?${params.toString()}`, { replace: true });
    }
  };

  const getClients = useCallback(async () => {
    if (!company?._id) {
      return;
    }
    setIsLoading(true);
    const data = await getCustomersService({
      companyId: company?._id,
      page,
      search,
      category,
      communications: "all",
      pageSize: 5,
    });
    setIsLoading(false);
    const customers = data?.customers ?? [];
    const count = data?.totalCustomers ?? 0;
    setClientsData(customers);
    setTotalCustomers(count);
    setPageNumber(Math.ceil(count / 5));
  }, [category, company?._id, page, search]);

  useEffect(() => {
    getClients();
  }, [getClients]);

  const handleNewCustomer = () => navigate("/clients/new");

  const getCategories = useCallback(async () => {
    setIsLoading(true);

    const data = await getCustomerCategoriesByPaginationService({
      companyId: company?._id,
      search: "",
      page: 1,
      pageSize: 1000,
    });

    setIsLoading(false);

    if (!data || data?.code === PERMISSION_CODE.PERMISSION_DENIED) {
      setCategories([]);
      return;
    }

    setCategories(
      [
        { _id: "0", name: translate("no-selected"), key: "000" },
        ...data?.categories,
      ] ?? [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?._id, userProfile?.user]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  useEffect(() => {
    if (company && company?.allow) {
      setCompanyAllow({ ...company?.allow });
    }
  }, [company]);

  if (userLoading || companyLoading) return null;

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <ClientsFilters
        category={category}
        search={search}
        categoryOptions={categories}
        onCategoryChange={setCategory}
        onSearchChange={setSearch}
        onNewCustomer={handleNewCustomer}
        onImport={toggleImportCustomers}
        translate={translate}
        allow={companyAllow}
      />

      {isLoading ? (
        <Loading />
      ) : clientsData.length === 0 ? (
        <NoResults
          message={
            IsAllow(companyAllow, "customer-02")
              ? translate("no-shop-found")
              : translate("no-clients-found")
          }
        />
      ) : (
        <>
          <ClientsTable
            clients={clientsData}
            allow={companyAllow}
            categories={categories}
            translate={translate}
            onViewClient={(id) => navigate(`/clients/${id}`)}
          />

          <Pagination
            page={page}
            pageNumber={pageNumber}
            setPage={handleUpdatePage}
            totalRecords={totalCustomers}
          />
        </>
      )}

      <ImportCustomer
        modalOpen={importModal}
        toggleModal={toggleImportCustomers}
        loadCustomer={getClients}
      />
    </div>
  );
};

export default TableClients;
